import { Injectable } from '@angular/core'
import { ChiliColorAll } from '../types'

@Injectable({
    providedIn: 'root',
})
export class ColorUtilsService {
    convertRgbToHsl(red: number, green: number, blue: number) {
        const r = red / 255
        const g = green / 255
        const b = blue / 255
        const max = Math.max(r, g, b)
        const min = Math.min(r, g, b)
        let h
        let s
        const l = (max + min) / 2

        if (max === min) {
            h = s = 0 // achromatic
        } else {
            const d = max - min
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0)
                    break
                case g:
                    h = (b - r) / d + 2
                    break
                case b:
                    h = (r - g) / d + 4
                    break
                default:
                    h = 0
            }
            h /= 6
        }
        return {
            h: h * 360,
            s: s * 100,
            l: l * 100,
        }
    }

    convertRGBtoHex(r: number, g: number, b: number) {
        return `#${this.toHex(r)}${this.toHex(g)}${this.toHex(b)}`
    }

    convertHexToRGB(hex: string) {
        let hexStr = hex.charAt(0) === '#' ? hex.substring(1, 7) : hex

        if (hexStr.length === 3) {
            hexStr =
                hexStr.substring(0, 1) +
                hexStr.substring(0, 1) +
                hexStr.substring(1, 1) +
                hexStr.substring(1, 1) +
                hexStr.substring(2, 1) +
                hexStr.substring(2, 1)
        }

        const r = parseInt(hexStr.slice(0, 2), 16)
        const g = parseInt(hexStr.slice(2, 4), 16)
        const b = parseInt(hexStr.slice(4, 6), 16)

        return {
            r: Number.isNaN(r) ? 0 : r,
            g: Number.isNaN(g) ? 0 : g,
            b: Number.isNaN(b) ? 0 : b,
        }
    }

    convertHexToRGBArray(hex: string) {
        const rgb = this.convertHexToRGB(hex)
        return [
            {
                name: 'Red',
                value: rgb.r,
            },
            {
                name: 'Green',
                value: rgb.g,
            },
            {
                name: 'Blue',
                value: rgb.b,
            },
        ]
    }

    convertHexToCMYK(hex: string) {
        const hexStr = hex.charAt(0) === '#' ? hex.substring(1, 7) : hex

        let { r, g, b } = this.convertHexToRGB(hexStr)

        if (r === 0 && g === 0 && b === 0) {
            return {
                c: 0,
                m: 0,
                y: 0,
                k: 1,
            }
        }

        r = r / 255
        g = g / 255
        b = b / 255

        const k = Math.min(1 - r, 1 - g, 1 - b)

        const c = (1 - r - k) / (1 - k)
        const m = (1 - g - k) / (1 - k)
        const y = (1 - b - k) / (1 - k)

        return {
            c: Math.round(c * 100),
            m: Math.round(m * 100),
            y: Math.round(y * 100),
            k: Math.round(k * 100),
        }
    }

    convertHslaToRgb(h: number, s: number, l: number, a: number = 1) {
        s /= 100
        l /= 100

        const c = (1 - Math.abs(2 * l - 1)) * s
        const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
        const m = l - c / 2

        let r = 0,
            g = 0,
            b = 0

        if (h >= 0 && h < 60) [r, g, b] = [c, x, 0]
        else if (h >= 60 && h < 120) [r, g, b] = [x, c, 0]
        else if (h >= 120 && h < 180) [r, g, b] = [0, c, x]
        else if (h >= 180 && h < 240) [r, g, b] = [0, x, c]
        else if (h >= 240 && h < 300) [r, g, b] = [x, 0, c]
        else if (h >= 300 && h < 360) [r, g, b] = [c, 0, x]

        return {
            r: Math.round((r + m) * 255),
            g: Math.round((g + m) * 255),
            b: Math.round((b + m) * 255),
            a,
        }
    }

    convertCmykToRgb(c: number, m: number, y: number, k: number) {
        c /= 100
        m /= 100
        y /= 100
        k /= 100

        const r = 255 * (1 - c) * (1 - k)
        const g = 255 * (1 - m) * (1 - k)
        const b = 255 * (1 - y) * (1 - k)

        return { r: Math.round(r), g: Math.round(g), b: Math.round(b) }
    }

    toOutput(r: number, g: number, b: number) {
        const rgbHex = this.convertRGBtoHex(r, g, b)
        const hex = rgbHex
        const rgb = { r, g, b }
        const hsl = this.convertRgbToHsl(r, g, b)
        const color: ChiliColorAll = {
            rgb,
            hsl,
            hex,
            type: 'ALL',
        }
        return {
            color,
            rgbHex,
        }
    }

    fromRgb(inputs: { value: number }[]) {
        const [r, g, b] = inputs.map((x) => x.value)
        return this.toOutput(r, g, b)
    }

    fromCmyk(inputs: { value: number }[]) {
        const [c, m, y, k] = inputs.map((x) => x.value / 100)

        const factor = 1 - k
        const r = 255 * (1 - c) * factor
        const g = 255 * (1 - m) * factor
        const b = 255 * (1 - y) * factor

        return this.toOutput(r, g, b)
    }

    private toHex(decimal: number) {
        const rounded = Math.round(decimal)
        return this.pad(rounded.toString(16), 2)
    }

    private pad(str: string, width: number): string {
        const prefix = '0'
        return str.length >= width ? str : new Array(width - str.length + 1).join(prefix) + str
    }
}
